import * as Moment from 'moment';

import {BehaviorSubject, Observable} from 'rxjs';
import {ObjectUtils, SECURED_STORE} from '../../../shared/utils';
import {
  PatientClientPhysician,
  PatientEditable,
} from '../models/order-entry/patient';

import {BillingInformation} from '../models/billing/billing-information';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import {FusionAuthService} from '../../../shared/services/fusion-auth';
import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ResponsibleParty} from '../models/order-entry/reponsible-party';
import {Router} from '@angular/router';
import {UrlProviderService} from '../../../shared/services/general';
import {retry, take} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  constructor(
    private urlProvider: UrlProviderService,
    private httpClient: HttpClient,
    private fusionAuthService: FusionAuthService,
    private router: Router
  ) {
  }

  public getPatientField(
    dataSource: BehaviorSubject<DataSource>,
    role: string,
    field: string,
    clientIds: number[],
    patientIds: number[],
    physicianIds: number[]
  ) {
    let url = 'advanced-search-patient';
    switch (field) {
      case 'lastname':
        url += '-last-name';
        break;
      case 'firstname':
        url += '-first-name';
        break;
      default:
        url += '-dob';
        break;
    }

    return SECURED_STORE({
      dataSource,
      loadUrl: `${this.urlProvider.ApiUrl}/v2/patients/${url}`,
      routerService: this.router,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      key: 'key',
      loadParams: {
        role,
        field,
        clientIds,
        physicianIds,
        patientIds,
      },
    });
  }

  public getPatient(id: number): Observable<PatientEditable> {
    return this.httpClient
      .get<PatientEditable>(`${this.urlProvider.ApiUrl}/v2/patients/${id}`)
      .pipe(
        retry(2),
      );
  }

  public checkPatientClients(
    patientId: number,
    clientIds: number[]
  ): Observable<boolean> {
    let url = `/v2/patients/check-clients?`;
    url += `patientId=${patientId}&`;

    const clientIdsArray: string[] = [];
    clientIds.map((x) => {
      clientIdsArray.push(`clientIds=${x}`);
    });

    if (clientIdsArray.length > 0) {
      url += clientIdsArray.join('&');
    }

    return this.httpClient
      .get<boolean>(`${this.urlProvider.ApiUrl}${url}`)
      .pipe(retry(2));
  }

  public isSsnUnique(ssn: string, patientId?: number): Observable<boolean> {
    const url = patientId
      ? `${this.urlProvider.ApiUrl}/v2/patients/validate-ssn?ssn=${ssn}&patientId=${patientId}`
      : `${this.urlProvider.ApiUrl}/records/patients/validate-ssn?ssn=${ssn}`;

    return this.httpClient.get<boolean>(url).pipe(retry(2));
  }

  public createPatient(patient: PatientEditable): Observable<PatientEditable> {
    patient = ObjectUtils.capitalize(patient) as PatientEditable;
    return this.httpClient.post<PatientEditable>(
      `${this.urlProvider.ApiUrl}/v2/patients`,
      patient
    );
  }

  public updatePatient(
    patientId: number,
    patient: PatientEditable
  ): Observable<PatientEditable> {
    patient = ObjectUtils.capitalize(patient) as PatientEditable;
    return this.httpClient.put<PatientEditable>(
      `${this.urlProvider.ApiUrl}/v2/patients/${patientId}`,
      patient
    );
  }

  public saveMedications(
    id: number,
    medicationIds: number[] = []
  ): Observable<PatientEditable> {
    return this.httpClient.post<PatientEditable>(
      `${this.urlProvider.ApiUrl}/v2/patients/${id}/save-medications`,
      medicationIds
    );
  }

  public saveDiagnoses(
    id: number,
    diagnosesIds: number[] = []
  ): Observable<PatientEditable> {
    return this.httpClient.post<PatientEditable>(
      `${this.urlProvider.ApiUrl}/v2/patients/${id}/save-diagnoses`,
      diagnosesIds
    );
  }

  public saveBillingInformation(
    id: number,
    billingInformation: BillingInformation[] = []
  ): Observable<PatientEditable> {
    return this.httpClient.post<PatientEditable>(
      `${this.urlProvider.ApiUrl}/v2/patients/${id}/save-billing-info`,
      {InsuranceInformation: billingInformation}
    );
  }

  public saveResponsibleParties(
    id: number,
    responsibleParties: ResponsibleParty[] = []
  ): Observable<PatientEditable> {
    return this.httpClient.post<PatientEditable>(
      `${this.urlProvider.ApiUrl}/v2/patients/${id}/save-responsible-parties`,
      {responsibleParties}
    );
  }

  public getPatientBillingInformation(
    patientId: number
  ): Observable<BillingInformation[]> {
    return this.httpClient
      .get<BillingInformation[]>(
        `${this.urlProvider.ApiUrl}/v2/patients/${patientId}/billing-info`
      )
      .pipe(retry(2));
  }

  public searchPatient(
    firstName: string,
    lastName: string,
    birthDate: string,
    socialSecurityNumber: string,
    clientIds: number[],
    physicianIds: number[],
    fromSearch: boolean = false,
    dataSource: BehaviorSubject<DataSource>
  ): CustomStore {
    const loadParams = {
      firstName: firstName !== null ? firstName : null,
      lastName: lastName !== null ? lastName : null,
      birthDate: birthDate,
      ssn: socialSecurityNumber !== null ? socialSecurityNumber : null,
      fromSearch,
      clientIds,
      physicianIds,
    };

    return SECURED_STORE({
      loadUrl: `${this.urlProvider.ApiUrl}/v2/patients/remote-operations`,
      loadParams,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      dataSource,
      routerService: this.router,
    });
  }

  public updatePatientClientPhysician(
    patient: PatientClientPhysician
  ): Observable<PatientEditable> {
    patient = ObjectUtils.capitalize(patient) as PatientClientPhysician;
    return this.httpClient.put<PatientEditable>(
      `${this.urlProvider.ApiUrl}/v2/patients/client-physician`,
      patient
    );
  }

  public store(
    clientIds: number[],
    dataSource: BehaviorSubject<DataSource>
  ): CustomStore {
    let url = `/v2/patients/remote-operations-all-entries`;
    if (clientIds.length > 0) {
      url = url + `?`;
      const clientIdsArray: string[] = [];
      clientIds.map((x) => {
        clientIdsArray.push(`clientIds=${x}`);
      });
      url += clientIdsArray.join('&');
    }

    return SECURED_STORE({
      dataSource,
      loadUrl: `${this.urlProvider.ApiUrl}${url}`,
      routerService: this.router,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      loadParams: undefined,
    });
  }

  public getByIds(patientIds: number[] = []): Observable<PatientEditable[]> {
    let url = `/v2/patients/ids?`;
    const patientIdsArray: string[] = [];
    patientIds.map((x) => {
      patientIdsArray.push(`ids=${x}`);
    });

    if (patientIdsArray.length > 0) {
      url += patientIdsArray.join('&');
    }

    return this.httpClient
      .get<PatientEditable[]>(`${this.urlProvider.ApiUrl}${url}`)
      .pipe(retry(2));
  }

  public sexualOrientations(
    dataSource: BehaviorSubject<DataSource>
  ): CustomStore {
    return SECURED_STORE({
      loadUrl: `${this.urlProvider.ApiUrl}/v2/patients/sexual-orientations`,
      loadParams: undefined,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      dataSource,
      routerService: this.router,
    });
  }

  public genderIdentities(
    dataSource: BehaviorSubject<DataSource>
  ): CustomStore {
    return SECURED_STORE({
      loadUrl: `${this.urlProvider.ApiUrl}/v2/patients/gender-identities`,
      loadParams: undefined,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      dataSource,
      routerService: this.router,
    });
  }
}
